import positioning from '../../data/dotsPositioning.json';
import { useIsDark } from '../../useIsDark';

/**
 * CompanyFunctionVerdictTable — the decision box for blog28, Figure 1.
 *
 * RULES_BLOG_POST Rule 37 (scannable verdict): a post whose headline poses a choice
 * must let a reader who skims only the boxes state the recommendation in one sentence.
 * That is why this is the FIRST figure in the post and why it is a table rather than a
 * chart: the content is a decision rule, not a distribution, so no chart type fits it.
 *
 * Column three says why, column four says how sure we are. The confidence column is our
 * judgement and the note under the table says so in plain words (Rule 36).
 *
 * Accepts `height` because ChartZoom clones its child with a taller height for the
 * lightbox; below 640px the rows stack, the situation becomes the heading and the three
 * answers sit under it (Rule 41 — a layout change, not a scale factor).
 */

type Row = {
  situation: string;
  recommendation: string;
  why: string;
  confidence: string;
};

export const CompanyFunctionVerdictTable = ({ height, dark }: { height?: number; dark?: boolean }) => {
  const liveDark = useIsDark();
  const isDark = dark ?? liveDark;

  const fig = (
    positioning as unknown as {
      figures: {
        verdict_table: { source: string; rows: Row[]; confidence_note: string };
      };
    }
  ).figures.verdict_table;

  const C = isDark
    ? { bg: '#0F172A', head: '#0B1220', border: '#334155', text: '#E2E8F0', muted: '#94A3B8' }
    : { bg: '#FFFFFF', head: '#F8FAFC', border: '#CBD5E1', text: '#334155', muted: '#64748B' };

  const teal = '#0AB3BF';

  return (
    <figure className="not-prose">
      <div
        className="rounded-xl overflow-hidden"
        style={{ border: `1px solid ${C.border}`, background: C.bg, minHeight: height }}
      >
        {/* Desktop: four columns. */}
        <table className="hidden sm:table w-full text-left text-sm" style={{ color: C.text }}>
          <thead>
            <tr style={{ background: C.head }}>
              <th className="px-3 py-2 font-semibold" style={{ borderBottom: `2px solid ${teal}` }}>
                Your situation
              </th>
              <th className="px-3 py-2 font-semibold" style={{ borderBottom: `2px solid ${teal}` }}>
                What we would do
              </th>
              <th className="px-3 py-2 font-semibold" style={{ borderBottom: `2px solid ${teal}` }}>
                Why
              </th>
              <th className="px-3 py-2 font-semibold" style={{ borderBottom: `2px solid ${teal}` }}>
                Stated confidence
              </th>
            </tr>
          </thead>
          <tbody>
            {fig.rows.map((r) => (
              <tr key={r.situation} style={{ borderTop: `1px solid ${C.border}` }}>
                <td className="px-3 py-3 align-top">{r.situation}</td>
                <td className="px-3 py-3 align-top font-semibold" style={{ color: teal }}>
                  {r.recommendation}
                </td>
                <td className="px-3 py-3 align-top">{r.why}</td>
                <td className="px-3 py-3 align-top" style={{ color: C.muted }}>
                  {r.confidence}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Phone: the same six rows, stacked (Rule 41). */}
        <div className="sm:hidden">
          {fig.rows.map((r) => (
            <div key={r.situation} className="px-3 py-3" style={{ borderTop: `1px solid ${C.border}` }}>
              <div className="text-[13px] font-semibold break-words" style={{ color: C.text }}>
                {r.situation}
              </div>
              <div className="text-[13px] font-semibold mt-1 break-words" style={{ color: teal }}>
                {r.recommendation}
              </div>
              <div className="text-[12px] mt-1 break-words" style={{ color: C.text }}>
                {r.why}
              </div>
              <div className="text-[11px] mt-1 break-words" style={{ color: C.muted }}>
                {r.confidence}
              </div>
            </div>
          ))}
        </div>
      </div>
      <figcaption className={`mt-3 text-xs leading-relaxed ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
        {fig.confidence_note}
      </figcaption>
    </figure>
  );
};
